/**
 * @file draft-po-list.tsx
 * @module app/modules/uProcure/po-management/draft-po-list
 * @description Draft PO tab of the Purchase Orders list: open draft POs of the selected
 *   warehouse, one per supplier (automatic from the Inventory Replenishment report, or manual). Click a draft to see
 *   its products and timeline.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import APICallService from "../../../../api/apiCallService";
import { PURCHASEORDER, reports } from "../../../../api/apiEndPoints";
import SyncRunSummary, { RunInfo } from "../inventory-replenishment/sync-run-summary";
import { POManagement } from "../../../../utils/constants";
import Method from "../../../../utils/methods";
import { DraftGroupBadge, draftGroupLabel } from "./draft-po-group";

/** Tab value of the Draft PO tab (not a PO status). */
export const DRAFT_PO_TAB = 100;
/** how often a running draft PO job is re-checked on this tab */
const RUN_POLL_MS = 10000;

export interface DraftPoSummary {
  _id: string;
  refKey: string;
  business: { reference: string; name: string } | null;
  supplierGroup?: { reference: string; name: string } | null;
  supplierHasGroups?: boolean;
  source: "auto" | "manual";
  products: number;
  totalQuantity: number;
  totalValue: number;
  missingCostPrice: number;
  totalWeightKg: number;
  tonnageTon: number | null;
  belowTonnage?: boolean;
  missingTon?: number;
  belowMoqCount?: number;
  oldPendingPoCount?: number;
  createdBy: { name: string; system: boolean };
  deleted: { by: { name: string }; at: string; reason: string } | null;
  _createdAt: string;
  _updatedAt: string;
}

export interface DraftPoListResponse {
  total: number;
  records: DraftPoSummary[];
  deletedCount: number;
  canEdit: boolean;
  canDelete: boolean;
}

export const fetchDraftPos = async (warehouse: string, searchTerm = "", deleted = false) => {
  const api = new APICallService(
    PURCHASEORDER.draftPurchaseOrders,
    { warehouse, ...(searchTerm ? { searchTerm } : {}), ...(deleted ? { view: "deleted" } : {}) },
    "",
    "",
    "",
    "",
    POManagement,
    warehouse
  );
  const res = await api.callAPI();
  return res && Array.isArray(res.records) ? (res as DraftPoListResponse) : null;
};

/** Soft deletes a draft; the reason is mandatory (at least 5 characters). */
export const deleteDraftPo = async (id: string, reason: string, warehouse?: string) => {
  const api = new APICallService(
    PURCHASEORDER.deleteDraftPurchaseOrder,
    { reason },
    { id, action: "delete" },
    "",
    "",
    "",
    POManagement,
    warehouse
  );
  return api.callAPI();
};

export const MIN_DELETE_REASON = 5;

type SortKey = "refKey" | "supplier" | "source" | "products" | "totalQuantity" | "totalValue" | "totalWeightKg" | "_createdAt" | "_updatedAt" | "deletedAt";

/** Asks for the mandatory reason, then deletes. */
export const DeleteDraftPoModal = ({
  draft,
  warehouse,
  onHide,
  onDeleted,
}: {
  draft: { _id: string; refKey: string } | null;
  warehouse?: string;
  onHide: () => void;
  onDeleted: () => void;
}) => {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => setReason(""), [draft?._id]);
  const valid = reason.trim().length >= MIN_DELETE_REASON;
  return (
    <Modal show={Boolean(draft)} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-20 fw-bolder">Delete draft PO {draft?.refKey}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="fs-14 text-gray-700">
          The draft is kept under &quot;Deleted&quot; with your name, the time and this reason. The automatic job will
          no longer use it.
        </p>
        <Form.Label className="fs-14 fw-600 required">Reason for deleting</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Supplier stopped this range, ordered by phone instead"
        />
        {!valid && reason.length > 0 && (
          <div className="fs-13 text-danger mt-1">Please write at least {MIN_DELETE_REASON} characters</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className="uw-btn uw-btn-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="danger"
          disabled={!valid || saving}
          onClick={async () => {
            if (!draft) return;
            setSaving(true);
            const res = await deleteDraftPo(draft._id, reason.trim(), warehouse);
            setSaving(false);
            if (res && res.status === "deleted") onDeleted();
          }}
        >
          {saving ? <Spinner animation="border" size="sm" /> : "Delete draft"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const money = (n: number) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const DraftPoList = ({
  warehouse,
  onCount,
  page = 1,
  pageLimit = 10,
  onTotal,
}: {
  warehouse?: string;
  onCount?: (n: number) => void;
  /** page and page size from the shared pagination of the PO list */
  page?: number;
  pageLimit?: number;
  /** rows matching the current search / view, for the shared pagination */
  onTotal?: (n: number) => void;
}) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<DraftPoSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);
  const [canDelete, setCanDelete] = useState(false);
  const [toDelete, setToDelete] = useState<DraftPoSummary | null>(null);
  const [reload, setReload] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>("_updatedAt");
  const [sortDir, setSortDir] = useState<1 | -1>(-1);

  const sorted = useMemo(() => {
    const val = (d: DraftPoSummary): string | number => {
      switch (sortKey) {
        case "refKey":
          return Number(d.refKey.replace(/\D/g, "")) || 0;
        case "supplier":
          return `${d.business?.name ?? ""} ${draftGroupLabel(d)}`.toLowerCase();
        case "source":
          return d.source;
        case "deletedAt":
          return d.deleted?.at ? new Date(d.deleted.at).getTime() : 0;
        case "_createdAt":
        case "_updatedAt":
          return new Date(d[sortKey]).getTime();
        default:
          return Number(d[sortKey]) || 0;
      }
    };
    return [...rows].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      return (x < y ? -1 : x > y ? 1 : 0) * sortDir || a.refKey.localeCompare(b.refKey);
    });
  }, [rows, sortKey, sortDir]);

  const sortBy = (k: SortKey) => {
    if (k === sortKey) setSortDir(sortDir === 1 ? -1 : 1);
    else {
      setSortKey(k);
      // text columns start A-Z, numbers and dates start highest / newest
      setSortDir(k === "supplier" || k === "source" ? 1 : -1);
    }
  };
  const Th = ({ k, label, end }: { k: SortKey; label: string; end?: boolean }) => (
    <th
      className={`cursor-pointer user-select-none text-nowrap ${end ? "text-end" : ""}`}
      onClick={() => sortBy(k)}
      title="Click to sort"
    >
      {label}
      <span className={sortKey === k ? "text-primary ms-1" : "text-gray-400 ms-1"}>
        {sortKey === k ? (sortDir === 1 ? "▲" : "▼") : "↕"}
      </span>
    </th>
  );

  useEffect(() => {
    if (!warehouse) return;
    let stale = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      const res = await fetchDraftPos(warehouse, search.trim(), showDeleted);
      if (!stale) {
        setRows(res?.records ?? []);
        onTotal?.(res?.records?.length ?? 0);
        setDeletedCount(res?.deletedCount ?? 0);
        setCanDelete(Boolean(res?.canDelete));
        if (!search.trim() && !showDeleted) onCount?.(res?.total ?? 0);
        setLoading(false);
      }
    }, 300);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [warehouse, search, showDeleted, reload]);

  // the last draft PO job (daily or Sync now): read once per warehouse; re-read only while it runs
  const [lastRun, setLastRun] = useState<RunInfo | null>(null);
  useEffect(() => {
    if (!warehouse) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const read = async () => {
      const api = new APICallService(reports.inventoryReplenishmentSyncStatus, { warehouse }, "", "", "", "", POManagement, warehouse);
      const res = (await api.callAPI()) as { current: RunInfo | null } | null;
      if (stop) return;
      const run = res?.current ?? null;
      setLastRun((prev) => {
        // a run that just finished changed the drafts: reload the list
        if (prev && (prev.status === "running" || (prev.status === "cancelled" && !prev.rolledBack)) && run && run.status !== "running") setReload((r) => r + 1);
        return run;
      });
      if (run && (run.status === "running" || (run.status === "cancelled" && !run.rolledBack))) timer = setTimeout(read, RUN_POLL_MS);
    };
    read();
    return () => {
      stop = true;
      if (timer) clearTimeout(timer);
    };
  }, [warehouse]);

  return (
    <div>
      {lastRun && !showDeleted && (
        <div className="mb-4">
          <SyncRunSummary run={lastRun} />
          {lastRun.status !== "done" && lastRun.status !== "running" && (
            <div className="fs-13 mt-1">
              <span role="button" className="text-primary fw-600" onClick={() => navigate("/UProcure/inventory-replenishment-report")}>
                Open the Inventory Replenishment Report
              </span>{" "}
              to run Sync now again.
            </div>
          )}
        </div>
      )}
      <div className="d-flex align-items-center justify-content-between mb-4 gap-4">
        <input
          type="text"
          className="form-control form-control-solid bg-white h-50px fs-14"
          style={{ maxWidth: 420 }}
          placeholder="Search draft no, supplier or product"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="d-flex align-items-center gap-3">
          <span className="fs-14 fw-600 text-gray-700">Show:</span>
          <div className="btn-group" role="group" aria-label="Show active or deleted drafts">
            <Button
              size="sm"
              variant={!showDeleted ? "primary" : "light"}
              onClick={() => setShowDeleted(false)}
              title="Drafts that can be edited and submitted"
            >
              Active drafts
            </Button>
            <Button
              size="sm"
              variant={showDeleted ? "danger" : "light"}
              onClick={() => setShowDeleted(true)}
              title="Soft deleted drafts: who deleted them, when and why"
            >
              Deleted ({deletedCount})
            </Button>
          </div>
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-row-bordered align-middle gs-5 gy-4 mb-0 fs-14">
          <thead>
            <tr className="fw-bold text-dark bg-light">
              <Th k="refKey" label="Draft No" />
              <Th k="supplier" label="Supplier" />
              <Th k="source" label="Source" />
              <Th k="products" label="Products" end />
              <Th k="totalQuantity" label="Total Qty" end />
              <Th k="totalValue" label="Value (TSh)" end />
              <Th k="totalWeightKg" label="Weight / Tonnage" end />
              <Th k="_createdAt" label="Created" />
              {showDeleted ? <Th k="deletedAt" label="Deleted" /> : <Th k="_updatedAt" label="Last Updated" />}
              {!showDeleted && canDelete && <th className="text-end">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={11} className="text-center py-10">
                  <Spinner animation="border" />
                </td>
              </tr>
            ) : !rows.length ? (
              <tr>
                <td colSpan={11} className="text-center py-10 text-gray-600">
                  {showDeleted ? "No deleted draft POs for this warehouse" : "No draft POs for this warehouse"}
                </td>
              </tr>
            ) : (
              sorted.slice((page - 1) * pageLimit, page * pageLimit).map((d) => (
                <tr
                  key={d._id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate("/UProcure/purchase-order-management/draft-purchase-order-details", { state: { id: d._id } })
                  }
                >
                  <td className="fw-bold">{d.refKey}</td>
                  <td>
                    {d.business ? (
                      <>
                        {d.business.name || "-"}
                        <DraftGroupBadge draft={d} />
                      </>
                    ) : (
                      <span className="badge badge-light-danger">No supplier</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge badge-light-${d.source === "auto" ? "primary" : "info"}`}>
                      {d.source === "auto" ? "Automatic (Replenishment report)" : "Manual"}
                    </span>
                  </td>
                  <td className="text-end">{d.products}</td>
                  <td className="text-end">{money(d.totalQuantity)}</td>
                  <td className="text-end">
                    {money(d.totalValue)}
                    {d.missingCostPrice > 0 && (
                      <div className="fs-12 text-danger">{d.missingCostPrice} without cost price</div>
                    )}
                  </td>
                  <td className="text-end text-nowrap">
                    {(d.totalWeightKg / 1000).toLocaleString("en-US", { maximumFractionDigits: 2 })} t
                    {d.tonnageTon ? (
                      <div className={`fs-12 fw-600 ${d.totalWeightKg / 1000 >= d.tonnageTon ? "text-success" : "text-warning"}`}>
                        of {d.tonnageTon} t ({Math.round((d.totalWeightKg / 10) / d.tonnageTon)}%)
                      </div>
                    ) : (
                      <div className="fs-12 text-gray-600">no tonnage set</div>
                    )}
                    {d.belowTonnage && <div className="badge badge-light-warning mt-1">Below tonnage</div>}
                    {Number(d.belowMoqCount) > 0 && <div className="badge badge-light-warning mt-1">{d.belowMoqCount} below MOQ</div>}
                    {Number(d.oldPendingPoCount) > 0 && (
                      <div className="badge badge-light-danger mt-1" title="Products already on a Pending PO older than the Settings old-PO age: super admin approval needed">
                        {d.oldPendingPoCount} on old Pending PO
                      </div>
                    )}
                  </td>
                  <td>
                    {Method.convertDateToDDMMYYYYHHMMAMPM(d._createdAt)}
                    <div className="fs-12 text-gray-600">{d.createdBy?.name}</div>
                  </td>
                  {showDeleted ? (
                    <td style={{ minWidth: 260 }}>
                      {d.deleted ? Method.convertDateToDDMMYYYYHHMMAMPM(d.deleted.at) : "-"}
                      <div className="fs-12 text-gray-700">by {d.deleted?.by?.name ?? "-"}</div>
                      <div className="fs-12 text-danger">Reason: {d.deleted?.reason ?? "-"}</div>
                    </td>
                  ) : (
                    <td>{Method.convertDateToDDMMYYYYHHMMAMPM(d._updatedAt)}</td>
                  )}
                  {!showDeleted && canDelete && (
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="light-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setToDelete(d);
                        }}
                      >
                        Delete
                      </Button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <DeleteDraftPoModal
        draft={toDelete}
        warehouse={warehouse}
        onHide={() => setToDelete(null)}
        onDeleted={() => {
          setToDelete(null);
          setReload((n) => n + 1);
        }}
      />
    </div>
  );
};

export default DraftPoList;
